import React, { useState } from 'react';
import {
  StepContainer,
  SectionHeader,
  SectionTitle,
  SectionDivider,
  SectionDescription,
  ButtonContainer,
  NextButton,
  SecondaryButton,
  InputRow,
  FormGroup,
  Label,
} from '../../styles';
import MobileInput from '../common/MobileInput';
import MobileSelect from '../common/MobileSelect';
import MoneyInput from '../common/MoneyInput';
import { MobileAnalysisFormData, SympathiseTenant, Partner, emptySympathiseTenant, emptyPartner, PoliticallyExposedPersonType } from '../../types';
import {
  maritalStatusOptions,
  employmentRelationshipOptions,
  genderOptions,
  nationalityOptions,
  politicallyExposedPersonOptions,
} from '../../constants';
import useDocumentLookup from '../../hooks/useDocumentLookup';
import { isCnpj, maskCnpj, normalizeDocument } from '../../../../shared/documents';
import styled from 'styled-components';

const SubSectionTitle = styled.h4`
  font-size: 18px;
  line-height: 1.25;
  font-weight: 600;
  color: #59249B;
  margin: 24px 0 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #C8ACFD;
`;

const ToggleRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 4px;
`;

const ToggleButton = styled.button<{ active: boolean }>`
  flex: 1;
  min-height: 46px;
  padding: 10px 12px;
  border-radius: 4px;
  border: 1px solid ${props => props.active ? '#59249B' : '#DDDDDD'};
  background: ${props => props.active ? '#59249B' : '#fff'};
  color: ${props => props.active ? '#fff' : '#6B6F77'};
  font-weight: ${props => props.active ? '600' : '400'};
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
`;

const DynamicCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 16px;
  margin-bottom: 12px;
  background: #fafafa;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const CardTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #555;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #e53935;
  font-size: 13px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  &:hover { background: #fff0f0; }
`;

const AddButton = styled.button`
  width: 100%;
  padding: 12px;
  border: 2px dashed #854EE0;
  background: none;
  border-radius: 8px;
  color: #854EE0;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 8px;
  &:hover { background: #f3eeff; }
`;

interface Step2Props {
  formData: MobileAnalysisFormData;
  updateFormData: (data: Partial<MobileAnalysisFormData>) => void;
  onNext: () => void;
  onBack: () => void;
}

// ─── PEP Sub-form (reutilizado em locatário, residente e solidários) ─────────
interface PepFieldsProps {
  pepValue: PoliticallyExposedPersonType;
  pepName: string;
  pepCpf: string;
  pepKinship: string;
  onPepChange: (value: PoliticallyExposedPersonType) => void;
  onPepNameChange: (v: string) => void;
  onPepCpfChange: (v: string) => void;
  onPepKinshipChange: (v: string) => void;
}

const PepFields: React.FC<PepFieldsProps> = ({
  pepValue, pepName, pepCpf, pepKinship,
  onPepChange, onPepNameChange, onPepCpfChange, onPepKinshipChange,
}) => (
  <>
    <MobileSelect
      label="Pessoa Politicamente Exposta?"
      value={pepValue}
      onChange={(v) => onPepChange(v as PoliticallyExposedPersonType)}
      options={politicallyExposedPersonOptions}
    />
    {pepValue === 'CLOSE_TO' && (
      <>
        <MobileInput
          label="Nome da Pessoa PEP"
          value={pepName}
          onChange={onPepNameChange}
          placeholder="Nome completo"
          required
        />
        <InputRow>
          <MobileInput
            label="CPF da Pessoa PEP"
            value={pepCpf}
            onChange={onPepCpfChange}
            placeholder="000.000.000-00"
            mask="999.999.999-99"
            inputMode="numeric"
            required
          />
          <MobileInput
            label="Grau de Parentesco"
            value={pepKinship}
            onChange={onPepKinshipChange}
            placeholder="Ex: Cônjuge, Filho"
            required
          />
        </InputRow>
      </>
    )}
  </>
);

// ─── Sympathise Tenant Card ───────────────────────────────────────────────────
interface SympathiseTenantCardProps {
  tenant: SympathiseTenant;
  index: number;
  onUpdate: (index: number, updates: Partial<SympathiseTenant>) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

const SympathiseTenantCard: React.FC<SympathiseTenantCardProps> = ({
  tenant, index, onUpdate, onRemove, canRemove,
}) => {
  const { lookup, isLoading } = useDocumentLookup();

  const handleCpfChange = async (cpf: string) => {
    onUpdate(index, { cpf });
    if (cpf.replace(/\D/g, '').length === 11) {
      const result = await lookup(cpf);
      if (result) {
        onUpdate(index, {
          cpf,
          name: result.name || tenant.name,
          birthDate: result.birthDate
            ? result.birthDate.includes('/')
              ? (() => { const [d, m, y] = result.birthDate.split('/'); return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`; })()
              : result.birthDate
            : tenant.birthDate,
          gender: result.gender?.toUpperCase() || tenant.gender,
          motherName: result.motherName || tenant.motherName,
        });
      }
    }
  };

  const requiresAdmissionDate = ['PRIVATE_SERVANT', 'CIVIL_SERVANT'].includes(tenant.employmentRelationship);
  const requiresPartner = ['CASADO', 'UNIAO_ESTAVEL'].includes(tenant.maritalStatus);

  return (
    <DynamicCard>
      <CardHeader>
        <CardTitle>Locatário Solidário #{index + 1}</CardTitle>
        {canRemove && (
          <RemoveButton type="button" onClick={() => onRemove(index)}>
            Remover
          </RemoveButton>
        )}
      </CardHeader>

      <MobileInput
        label="CPF"
        value={tenant.cpf}
        onChange={handleCpfChange}
        placeholder="000.000.000-00"
        mask="999.999.999-99"
        required
        loading={isLoading}
        inputMode="numeric"
      />
      <MobileInput
        label="Nome Completo"
        value={tenant.name}
        onChange={(v) => onUpdate(index, { name: v })}
        placeholder="Nome completo"
        required
      />
      <InputRow>
        <MobileInput
          label="Data de Nascimento"
          value={tenant.birthDate}
          onChange={(v) => onUpdate(index, { birthDate: v })}
          type="date"
          required
        />
        <MobileSelect
          label="Gênero"
          value={tenant.gender}
          onChange={(v) => onUpdate(index, { gender: v })}
          options={genderOptions}
        />
      </InputRow>
      <MobileInput
        label="Nome da Mãe"
        value={tenant.motherName}
        onChange={(v) => onUpdate(index, { motherName: v })}
        placeholder="Nome completo da mãe"
        required
      />
      <InputRow>
        <MobileInput
          label="E-mail"
          value={tenant.email}
          onChange={(v) => onUpdate(index, { email: v })}
          placeholder="email@exemplo.com"
          type="email"
          required
          inputMode="email"
        />
        <MobileInput
          label="Telefone"
          value={tenant.phone}
          onChange={(v) => onUpdate(index, { phone: v })}
          placeholder="(00) 00000-0000"
          mask="(99) 99999-9999"
          required
          inputMode="tel"
        />
      </InputRow>
      <InputRow>
        <MobileSelect
          label="Estado Civil"
          value={tenant.maritalStatus}
          onChange={(v) => onUpdate(index, { maritalStatus: v })}
          options={maritalStatusOptions}
          required
        />
        <MobileSelect
          label="Nacionalidade"
          value={tenant.nationality}
          onChange={(v) => onUpdate(index, { nationality: v })}
          options={nationalityOptions}
        />
      </InputRow>
      <MobileSelect
        label="Vínculo Empregatício"
        value={tenant.employmentRelationship}
        onChange={(v) => onUpdate(index, { employmentRelationship: v })}
        options={employmentRelationshipOptions}
        required
      />
      {requiresAdmissionDate && (
        <MobileInput
          label="Data de Admissão"
          value={''}
          onChange={() => {}}
          type="date"
          required
        />
      )}
      <MobileInput
        label="Profissão"
        value={tenant.profession}
        onChange={(v) => onUpdate(index, { profession: v })}
        placeholder="Ex: Engenheiro, Médico"
        required
      />
      <InputRow>
        <MoneyInput
          label="Renda Mensal"
          value={tenant.monthlyFixedIncome}
          onChange={(v) => onUpdate(index, { monthlyFixedIncome: v })}
          required
        />
        <MoneyInput
          label="Outras Rendas"
          value={tenant.otherIncomes}
          onChange={(v) => onUpdate(index, { otherIncomes: v })}
        />
      </InputRow>

      <FormGroup style={{ marginTop: 8 }}>
        <Label>Residente no imóvel?</Label>
        <ToggleRow>
          <ToggleButton type="button" active={tenant.resident} onClick={() => onUpdate(index, { resident: true })}>
            Sim
          </ToggleButton>
          <ToggleButton type="button" active={!tenant.resident} onClick={() => onUpdate(index, { resident: false })}>
            Não
          </ToggleButton>
        </ToggleRow>
      </FormGroup>

      <PepFields
        pepValue={tenant.politicallyExposedPerson}
        pepName={tenant.pepName}
        pepCpf={tenant.pepCpf}
        pepKinship={tenant.pepKinship}
        onPepChange={(v) => onUpdate(index, { politicallyExposedPerson: v })}
        onPepNameChange={(v) => onUpdate(index, { pepName: v })}
        onPepCpfChange={(v) => onUpdate(index, { pepCpf: v })}
        onPepKinshipChange={(v) => onUpdate(index, { pepKinship: v })}
      />
    </DynamicCard>
  );
};

// ─── Partner Card (PJ) ────────────────────────────────────────────────────────
interface PartnerCardProps {
  partner: Partner;
  index: number;
  onUpdate: (index: number, updates: Partial<Partner>) => void;
  onRemove: (index: number) => void;
  canRemove: boolean;
}

const PartnerCard: React.FC<PartnerCardProps> = ({
  partner, index, onUpdate, onRemove, canRemove,
}) => {
  const { lookup, isLoading } = useDocumentLookup();

  const handleCpfChange = async (cpf: string) => {
    onUpdate(index, { cpf });
    if (cpf.replace(/\D/g, '').length === 11) {
      const result = await lookup(cpf);
      if (result?.name) {
        onUpdate(index, { cpf, name: result.name });
      }
    }
  };

  return (
    <DynamicCard>
      <CardHeader>
        <CardTitle>Sócio/Acionista #{index + 1}</CardTitle>
        {canRemove && (
          <RemoveButton type="button" onClick={() => onRemove(index)}>
            Remover
          </RemoveButton>
        )}
      </CardHeader>
      <InputRow>
        <MobileInput
          label="CPF"
          value={partner.cpf}
          onChange={handleCpfChange}
          placeholder="000.000.000-00"
          mask="999.999.999-99"
          required
          loading={isLoading}
          inputMode="numeric"
        />
        <MobileInput
          label="Nome"
          value={partner.name}
          onChange={(v) => onUpdate(index, { name: v })}
          placeholder="Nome completo"
          required
        />
      </InputRow>
      <InputRow>
        <MobileInput
          label="% Capital Social"
          value={partner.percentCapital}
          onChange={(v) => onUpdate(index, { percentCapital: v })}
          placeholder="Ex: 50"
          inputMode="numeric"
          required
        />
        <MobileInput
          label="Data de Entrada"
          value={partner.dateOfEntry}
          onChange={(v) => onUpdate(index, { dateOfEntry: v })}
          type="date"
          required
        />
      </InputRow>
    </DynamicCard>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const Step2_TenantData: React.FC<Step2Props> = ({
  formData,
  updateFormData,
  onNext,
  onBack,
}) => {
  const { lookup, isLoading, error: lookupError } = useDocumentLookup();
  const [autoFilledFields, setAutoFilledFields] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isNaturalPerson = formData.tenantType === 'NATURAL';
  const nt = formData.naturalTenant;
  const lt = formData.legalTenant;

  const requiresAdmissionDate = ['PRIVATE_SERVANT', 'CIVIL_SERVANT'].includes(nt.employmentRelationship);
  const requiresPartner = ['CASADO', 'UNIAO_ESTAVEL'].includes(nt.maritalStatus);

  // ── CPF lookup for main natural tenant ──
  const handleCpfChange = async (cpf: string) => {
    updateFormData({ naturalTenant: { ...nt, cpf } });
    if (cpf.replace(/\D/g, '').length === 11) {
      const result = await lookup(cpf);
      if (result) {
        const filled: string[] = [];
        const updates: any = { ...nt, cpf };
        if (result.name) { updates.name = result.name; filled.push('name'); }
        if (result.birthDate) {
          let d = result.birthDate;
          if (d.includes('/')) { const [dd, mm, yy] = d.split('/'); d = `${yy}-${mm.padStart(2,'0')}-${dd.padStart(2,'0')}`; }
          updates.birthDate = d; filled.push('birthDate');
        }
        if (result.gender) { updates.gender = result.gender.toUpperCase(); filled.push('gender'); }
        if (result.motherName) { updates.motherName = result.motherName; filled.push('motherName'); }
        updateFormData({ naturalTenant: updates });
        setAutoFilledFields(filled);
      }
    }
  };

  // ── CNPJ lookup ──
  const handleCnpjChange = async (cnpj: string) => {
    updateFormData({ legalTenant: { ...lt, cnpj } });
    if (normalizeDocument(cnpj).length === 14) {
      const result = await lookup(cnpj);
      if (result) {
        const filled: string[] = [];
        const updates: any = { ...lt, cnpj };
        if (result.companyName) { updates.companyName = result.companyName; filled.push('companyName'); }
        if (result.foundationDate) { updates.foundationDate = result.foundationDate; filled.push('foundationDate'); }
        if (result.shareCapital) { updates.shareCapital = result.shareCapital; filled.push('shareCapital'); }
        if (result.phone) { updates.phone = result.phone; filled.push('phone'); }
        if (result.email) { updates.email = result.email; filled.push('email'); }
        updateFormData({ legalTenant: updates });
        setAutoFilledFields(filled);
      }
    }
  };

  // ── Sympathise tenants helpers ──
  const updateSympathiseTenant = (index: number, updates: Partial<SympathiseTenant>) => {
    const updated = nt.sympathiseTenants.map((t, i) => i === index ? { ...t, ...updates } : t);
    updateFormData({ naturalTenant: { ...nt, sympathiseTenants: updated } });
  };
  const addSympathiseTenant = () => {
    updateFormData({ naturalTenant: { ...nt, sympathiseTenants: [...nt.sympathiseTenants, { ...emptySympathiseTenant }] } });
  };
  const removeSympathiseTenant = (index: number) => {
    updateFormData({ naturalTenant: { ...nt, sympathiseTenants: nt.sympathiseTenants.filter((_, i) => i !== index) } });
  };

  // ── Partners helpers ──
  const updatePartner = (index: number, updates: Partial<Partner>) => {
    const updated = lt.partners.map((p, i) => i === index ? { ...p, ...updates } : p);
    updateFormData({ legalTenant: { ...lt, partners: updated } });
  };
  const addPartner = () => {
    updateFormData({ legalTenant: { ...lt, partners: [...lt.partners, { ...emptyPartner }] } });
  };
  const removePartner = (index: number) => {
    updateFormData({ legalTenant: { ...lt, partners: lt.partners.filter((_, i) => i !== index) } });
  };

  // ── Validation ──
  const validateNaturalPerson = (): boolean => {
    const e: Record<string, string> = {};
    if (!nt.cpf || nt.cpf.replace(/\D/g, '').length !== 11) e.cpf = 'CPF inválido';
    if (!nt.name?.trim()) e.name = 'Nome é obrigatório';
    if (!nt.email?.trim()) e.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nt.email)) e.email = 'E-mail inválido';
    if (!nt.phone || nt.phone.replace(/\D/g, '').length < 10) e.phone = 'Telefone inválido';
    if (!nt.birthDate) e.birthDate = 'Data de nascimento é obrigatória';
    if (!nt.maritalStatus) e.maritalStatus = 'Estado civil é obrigatório';
    if (!nt.employmentRelationship) e.employmentRelationship = 'Vínculo empregatício é obrigatório';
    if (requiresAdmissionDate && !nt.employmentAdmissionDate) e.employmentAdmissionDate = 'Data de admissão é obrigatória';
    if (!nt.profession?.trim()) e.profession = 'Profissão é obrigatória';
    if (!nt.isResident) {
      if (!nt.residentCpf || nt.residentCpf.replace(/\D/g, '').length !== 11) e.residentCpf = 'CPF do residente inválido';
      if (!nt.residentName?.trim()) e.residentName = 'Nome do residente é obrigatório';
      if (!nt.residentEmploymentRelationship) e.residentEmploymentRelationship = 'Vínculo empregatício do residente é obrigatório';
    }
    if (requiresPartner) {
      if (!nt.partnerCpf || nt.partnerCpf.replace(/\D/g, '').length !== 11) e.partnerCpf = 'CPF do cônjuge inválido';
      if (!nt.partnerName?.trim()) e.partnerName = 'Nome do cônjuge é obrigatório';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateLegalPerson = (): boolean => {
    const e: Record<string, string> = {};
    if (!lt.cnpj || !isCnpj(lt.cnpj)) e.cnpj = 'CNPJ inválido';
    if (!lt.companyName?.trim()) e.companyName = 'Razão social é obrigatória';
    if (!lt.email?.trim()) e.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lt.email)) e.email = 'E-mail inválido';
    if (!lt.phone || lt.phone.replace(/\D/g, '').length < 10) e.phone = 'Telefone inválido';
    if (!lt.foundationDate) e.foundationDate = 'Data de fundação é obrigatória';
    if (!lt.contactName?.trim()) e.contactName = 'Nome do contato é obrigatório';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    const valid = isNaturalPerson ? validateNaturalPerson() : validateLegalPerson();
    if (valid) onNext();
  };

  // ── Resident CPF lookup ──
  const { lookup: residentLookup, isLoading: residentLoading } = useDocumentLookup();
  const handleResidentCpfChange = async (cpf: string) => {
    updateFormData({ naturalTenant: { ...nt, residentCpf: cpf } });
    if (cpf.replace(/\D/g, '').length === 11) {
      const result = await residentLookup(cpf);
      if (result) {
        let d = result.birthDate || '';
        if (d.includes('/')) { const [dd, mm, yy] = d.split('/'); d = `${yy}-${mm.padStart(2,'0')}-${dd.padStart(2,'0')}`; }
        updateFormData({
          naturalTenant: {
            ...nt,
            residentCpf: cpf,
            residentName: result.name || nt.residentName,
            residentBirthDate: d || nt.residentBirthDate,
            residentGender: result.gender?.toUpperCase() || nt.residentGender,
            residentMotherName: result.motherName || nt.residentMotherName,
          },
        });
      }
    }
  };

  return (
    <StepContainer>
      <SectionHeader>
        <SectionTitle>
          {isNaturalPerson ? 'Dados do Locatário' : 'Dados da Empresa'}
        </SectionTitle>
        <SectionDivider />
      </SectionHeader>

      <SectionDescription>
        {isNaturalPerson
          ? 'Informe o CPF para buscar os dados automaticamente.'
          : 'Informe o CNPJ para buscar os dados automaticamente.'}
      </SectionDescription>

      {/* ================================================================ */}
      {/* PESSOA FÍSICA */}
      {/* ================================================================ */}
      {isNaturalPerson ? (
        <>
          {/* ── Informações Pessoais ── */}
          <SubSectionTitle>Informações Pessoais</SubSectionTitle>

          <MobileInput
            label="CPF"
            value={nt.cpf}
            onChange={handleCpfChange}
            placeholder="000.000.000-00"
            mask="999.999.999-99"
            required
            error={errors.cpf}
            loading={isLoading}
            inputMode="numeric"
          />
          <MobileInput
            label="Nome Completo"
            value={nt.name}
            onChange={(v) => updateFormData({ naturalTenant: { ...nt, name: v } })}
            placeholder="Digite o nome completo"
            required
            error={errors.name}
            autoFilled={autoFilledFields.includes('name')}
          />
          <InputRow>
            <MobileInput
              label="E-mail"
              value={nt.email}
              onChange={(v) => updateFormData({ naturalTenant: { ...nt, email: v } })}
              placeholder="email@exemplo.com"
              type="email"
              required
              error={errors.email}
              inputMode="email"
            />
            <MobileInput
              label="Telefone"
              value={nt.phone}
              onChange={(v) => updateFormData({ naturalTenant: { ...nt, phone: v } })}
              placeholder="(00) 00000-0000"
              mask="(99) 99999-9999"
              required
              error={errors.phone}
              inputMode="tel"
            />
          </InputRow>
          <InputRow>
            <MobileInput
              label="Data de Nascimento"
              value={nt.birthDate}
              onChange={(v) => updateFormData({ naturalTenant: { ...nt, birthDate: v } })}
              type="date"
              required
              error={errors.birthDate}
              autoFilled={autoFilledFields.includes('birthDate')}
            />
            <MobileSelect
              label="Gênero"
              value={nt.gender}
              onChange={(v) => updateFormData({ naturalTenant: { ...nt, gender: v } })}
              options={genderOptions}
              error={errors.gender}
            />
          </InputRow>
          <MobileInput
            label="Nome da Mãe"
            value={nt.motherName}
            onChange={(v) => updateFormData({ naturalTenant: { ...nt, motherName: v } })}
            placeholder="Nome completo da mãe"
            required
            error={errors.motherName}
            autoFilled={autoFilledFields.includes('motherName')}
          />
          <InputRow>
            <MobileSelect
              label="Estado Civil"
              value={nt.maritalStatus}
              onChange={(v) => updateFormData({ naturalTenant: { ...nt, maritalStatus: v } })}
              options={maritalStatusOptions}
              required
              error={errors.maritalStatus}
            />
            <MobileSelect
              label="Nacionalidade"
              value={nt.nationality}
              onChange={(v) => updateFormData({ naturalTenant: { ...nt, nationality: v } })}
              options={nationalityOptions}
            />
          </InputRow>

          {/* Nome Social */}
          <FormGroup>
            <Label>Possui nome social?</Label>
            <ToggleRow>
              <ToggleButton type="button" active={nt.hasSocialName} onClick={() => updateFormData({ naturalTenant: { ...nt, hasSocialName: true } })}>
                Sim
              </ToggleButton>
              <ToggleButton type="button" active={!nt.hasSocialName} onClick={() => updateFormData({ naturalTenant: { ...nt, hasSocialName: false, socialName: '' } })}>
                Não
              </ToggleButton>
            </ToggleRow>
          </FormGroup>
          {nt.hasSocialName && (
            <MobileInput
              label="Nome Social"
              value={nt.socialName}
              onChange={(v) => updateFormData({ naturalTenant: { ...nt, socialName: v } })}
              placeholder="Nome social"
            />
          )}

          {/* PEP */}
          <PepFields
            pepValue={nt.politicallyExposedPerson}
            pepName={nt.pepName}
            pepCpf={nt.pepCpf}
            pepKinship={nt.pepKinship}
            onPepChange={(v) => updateFormData({ naturalTenant: { ...nt, politicallyExposedPerson: v } })}
            onPepNameChange={(v) => updateFormData({ naturalTenant: { ...nt, pepName: v } })}
            onPepCpfChange={(v) => updateFormData({ naturalTenant: { ...nt, pepCpf: v } })}
            onPepKinshipChange={(v) => updateFormData({ naturalTenant: { ...nt, pepKinship: v } })}
          />

          {/* ── Informações Profissionais ── */}
          <SubSectionTitle>Informações Profissionais</SubSectionTitle>
          <MobileSelect
            label="Vínculo Empregatício"
            value={nt.employmentRelationship}
            onChange={(v) => updateFormData({ naturalTenant: { ...nt, employmentRelationship: v } })}
            options={employmentRelationshipOptions}
            required
            error={errors.employmentRelationship}
          />
          {requiresAdmissionDate && (
            <MobileInput
              label="Data de Admissão"
              value={nt.employmentAdmissionDate || ''}
              onChange={(v) => updateFormData({ naturalTenant: { ...nt, employmentAdmissionDate: v } })}
              type="date"
              required
              error={errors.employmentAdmissionDate}
            />
          )}
          <MobileInput
            label="Profissão"
            value={nt.profession}
            onChange={(v) => updateFormData({ naturalTenant: { ...nt, profession: v } })}
            placeholder="Ex: Engenheiro, Médico, Professor"
            required
            error={errors.profession}
          />

          {/* ── Cônjuge (condicional) ── */}
          {requiresPartner && (
            <>
              <SubSectionTitle>Dados do Cônjuge</SubSectionTitle>
              <MobileInput
                label="CPF do Cônjuge"
                value={nt.partnerCpf}
                onChange={(v) => updateFormData({ naturalTenant: { ...nt, partnerCpf: v } })}
                placeholder="000.000.000-00"
                mask="999.999.999-99"
                required
                error={errors.partnerCpf}
                inputMode="numeric"
              />
              <MobileInput
                label="Nome do Cônjuge"
                value={nt.partnerName}
                onChange={(v) => updateFormData({ naturalTenant: { ...nt, partnerName: v } })}
                placeholder="Nome completo"
                required
                error={errors.partnerName}
              />
            </>
          )}

          {/* ── Residente ── */}
          <SubSectionTitle>Residente no Imóvel</SubSectionTitle>
          <FormGroup>
            <Label>O locatário irá residir no imóvel?</Label>
            <ToggleRow>
              <ToggleButton type="button" active={nt.isResident} onClick={() => updateFormData({ naturalTenant: { ...nt, isResident: true } })}>
                Sim
              </ToggleButton>
              <ToggleButton type="button" active={!nt.isResident} onClick={() => updateFormData({ naturalTenant: { ...nt, isResident: false } })}>
                Não
              </ToggleButton>
            </ToggleRow>
          </FormGroup>

          {!nt.isResident && (
            <>
              <MobileInput
                label="CPF do Residente"
                value={nt.residentCpf}
                onChange={handleResidentCpfChange}
                placeholder="000.000.000-00"
                mask="999.999.999-99"
                required
                error={errors.residentCpf}
                loading={residentLoading}
                inputMode="numeric"
              />
              <MobileInput
                label="Nome do Residente"
                value={nt.residentName}
                onChange={(v) => updateFormData({ naturalTenant: { ...nt, residentName: v } })}
                placeholder="Nome completo"
                required
                error={errors.residentName}
              />
              <InputRow>
                <MobileInput
                  label="Data de Nascimento"
                  value={nt.residentBirthDate}
                  onChange={(v) => updateFormData({ naturalTenant: { ...nt, residentBirthDate: v } })}
                  type="date"
                  required
                />
                <MobileSelect
                  label="Gênero"
                  value={nt.residentGender}
                  onChange={(v) => updateFormData({ naturalTenant: { ...nt, residentGender: v } })}
                  options={genderOptions}
                />
              </InputRow>
              <MobileInput
                label="Nome da Mãe"
                value={nt.residentMotherName}
                onChange={(v) => updateFormData({ naturalTenant: { ...nt, residentMotherName: v } })}
                placeholder="Nome completo da mãe"
                required
              />
              <InputRow>
                <MobileInput
                  label="E-mail"
                  value={nt.residentEmail}
                  onChange={(v) => updateFormData({ naturalTenant: { ...nt, residentEmail: v } })}
                  placeholder="email@exemplo.com"
                  type="email"
                  required
                  inputMode="email"
                />
                <MobileInput
                  label="Telefone"
                  value={nt.residentPhone}
                  onChange={(v) => updateFormData({ naturalTenant: { ...nt, residentPhone: v } })}
                  placeholder="(00) 00000-0000"
                  mask="(99) 99999-9999"
                  required
                  inputMode="tel"
                />
              </InputRow>
              <InputRow>
                <MobileSelect
                  label="Estado Civil"
                  value={nt.residentMaritalStatus}
                  onChange={(v) => updateFormData({ naturalTenant: { ...nt, residentMaritalStatus: v } })}
                  options={maritalStatusOptions}
                  required
                />
                <MobileSelect
                  label="Nacionalidade"
                  value={nt.residentNationality}
                  onChange={(v) => updateFormData({ naturalTenant: { ...nt, residentNationality: v } })}
                  options={nationalityOptions}
                />
              </InputRow>
              <MobileSelect
                label="Vínculo Empregatício"
                value={nt.residentEmploymentRelationship}
                onChange={(v) => updateFormData({ naturalTenant: { ...nt, residentEmploymentRelationship: v } })}
                options={employmentRelationshipOptions}
                required
                error={errors.residentEmploymentRelationship}
              />
              <MobileInput
                label="Profissão"
                value={nt.residentProfession}
                onChange={(v) => updateFormData({ naturalTenant: { ...nt, residentProfession: v } })}
                placeholder="Ex: Engenheiro, Médico"
                required
              />
              <PepFields
                pepValue={nt.residentPoliticallyExposedPerson}
                pepName={nt.residentPepName}
                pepCpf={nt.residentPepCpf}
                pepKinship={nt.residentPepKinship}
                onPepChange={(v) => updateFormData({ naturalTenant: { ...nt, residentPoliticallyExposedPerson: v } })}
                onPepNameChange={(v) => updateFormData({ naturalTenant: { ...nt, residentPepName: v } })}
                onPepCpfChange={(v) => updateFormData({ naturalTenant: { ...nt, residentPepCpf: v } })}
                onPepKinshipChange={(v) => updateFormData({ naturalTenant: { ...nt, residentPepKinship: v } })}
              />
            </>
          )}

          {/* ── Locatários Solidários ── */}
          <SubSectionTitle>Locatários Solidários</SubSectionTitle>
          <FormGroup>
            <Label>Adicionar locatário solidário?</Label>
            <ToggleRow>
              <ToggleButton
                type="button"
                active={nt.includeSympathiseTenants}
                onClick={() => updateFormData({ naturalTenant: { ...nt, includeSympathiseTenants: true } })}
              >
                Sim
              </ToggleButton>
              <ToggleButton
                type="button"
                active={!nt.includeSympathiseTenants}
                onClick={() => updateFormData({ naturalTenant: { ...nt, includeSympathiseTenants: false } })}
              >
                Não
              </ToggleButton>
            </ToggleRow>
          </FormGroup>

          {nt.includeSympathiseTenants && (
            <>
              {nt.sympathiseTenants.map((tenant, index) => (
                <SympathiseTenantCard
                  key={index}
                  tenant={tenant}
                  index={index}
                  onUpdate={updateSympathiseTenant}
                  onRemove={removeSympathiseTenant}
                  canRemove={nt.sympathiseTenants.length > 1}
                />
              ))}
              <AddButton type="button" onClick={addSympathiseTenant}>
                + Adicionar Locatário Solidário
              </AddButton>
            </>
          )}
        </>
      ) : (
        /* ================================================================ */
        /* PESSOA JURÍDICA */
        /* ================================================================ */
        <>
          {/* ── Identificação ── */}
          <SubSectionTitle>Identificação da Empresa</SubSectionTitle>

          <MobileInput
            label="CNPJ"
            value={lt.cnpj}
            onChange={handleCnpjChange}
            placeholder="00.000.000/0000-00"
            mask="99.999.999/9999-99"
            required
            error={errors.cnpj}
            loading={isLoading}
            inputMode="numeric"
          />
          <MobileInput
            label="Razão Social"
            value={lt.companyName}
            onChange={(v) => updateFormData({ legalTenant: { ...lt, companyName: v } })}
            placeholder="Razão social da empresa"
            required
            error={errors.companyName}
            autoFilled={autoFilledFields.includes('companyName')}
          />
          <InputRow>
            <MobileInput
              label="Data de Constituição"
              value={lt.foundationDate}
              onChange={(v) => updateFormData({ legalTenant: { ...lt, foundationDate: v } })}
              type="date"
              required
              error={errors.foundationDate}
              autoFilled={autoFilledFields.includes('foundationDate')}
            />
            <MobileInput
              label="Últ. Alt. Contratual"
              value={lt.lastContractualAlterationDate}
              onChange={(v) => updateFormData({ legalTenant: { ...lt, lastContractualAlterationDate: v } })}
              type="date"
            />
          </InputRow>
          <InputRow>
            <MoneyInput
              label="Capital Social"
              value={lt.shareCapital}
              onChange={(v) => updateFormData({ legalTenant: { ...lt, shareCapital: v } })}
              error={errors.shareCapital}
            />
            <MoneyInput
              label="Capital Integralizado"
              value={lt.paidInCapital}
              onChange={(v) => updateFormData({ legalTenant: { ...lt, paidInCapital: v } })}
            />
          </InputRow>
          <MoneyInput
            label="Faturamento Anual"
            value={lt.anualRevenue}
            onChange={(v) => updateFormData({ legalTenant: { ...lt, anualRevenue: v } })}
          />
          <InputRow>
            <MobileInput
              label="E-mail"
              value={lt.email}
              onChange={(v) => updateFormData({ legalTenant: { ...lt, email: v } })}
              placeholder="email@empresa.com"
              type="email"
              required
              error={errors.email}
              autoFilled={autoFilledFields.includes('email')}
              inputMode="email"
            />
            <MobileInput
              label="Telefone"
              value={lt.phone}
              onChange={(v) => updateFormData({ legalTenant: { ...lt, phone: v } })}
              placeholder="(00) 0000-0000"
              mask="(99) 99999-9999"
              required
              error={errors.phone}
              autoFilled={autoFilledFields.includes('phone')}
              inputMode="tel"
            />
          </InputRow>
          <InputRow>
            <MobileInput
              label="Nome do Contato"
              value={lt.contactName}
              onChange={(v) => updateFormData({ legalTenant: { ...lt, contactName: v } })}
              placeholder="Nome do responsável"
              required
              error={errors.contactName}
            />
            <MobileInput
              label="Telefone do Contato"
              value={lt.contactPhone}
              onChange={(v) => updateFormData({ legalTenant: { ...lt, contactPhone: v } })}
              placeholder="(00) 00000-0000"
              mask="(99) 99999-9999"
              inputMode="tel"
            />
          </InputRow>

          {/* ── Sócios/Acionistas ── */}
          <SubSectionTitle>Sócios / Acionistas</SubSectionTitle>
          {lt.partners.map((partner, index) => (
            <PartnerCard
              key={index}
              partner={partner}
              index={index}
              onUpdate={updatePartner}
              onRemove={removePartner}
              canRemove={lt.partners.length > 1}
            />
          ))}
          <AddButton type="button" onClick={addPartner}>
            + Adicionar Sócio/Acionista
          </AddButton>
        </>
      )}

      <ButtonContainer>
        <SecondaryButton type="button" onClick={onBack}>
          Voltar
        </SecondaryButton>
        <NextButton type="button" onClick={handleNext}>
          Próximo
        </NextButton>
      </ButtonContainer>
    </StepContainer>
  );
};

export default Step2_TenantData;
